import { feature } from '@turf/turf';
import { type LayerProps, Layer as ReactLayer, Source, type SourceProps } from 'react-map-gl/mapbox';
import { getPaintColorsV2, getSourceId } from 'sonata-static-storage/lib/helpers/map.helpers';

import type { FeatureCollection, Polygon } from 'geojson';
import type { ExtendedLayer } from 'sonata-static-storage/lib/common/common.types';
import type { LayerMapData } from 'sonata-static-storage/lib/common/itemsGrouping.types';
import type { MapViewerFilters, MapViewerSelectedItems } from '../mapViewer.types';

type Props = {
  filters: MapViewerFilters;
  layerMapData: LayerMapData[];
  layer: ExtendedLayer;
  selectedItems: MapViewerSelectedItems;
};

const TOTAL_WEIGHT = 100;

export function CustomLayer({ layer, layerMapData, selectedItems }: Props): JSX.Element {
  const { config, poiFilters } = layer;
  const id = config._id;
  const sourceId = `${id}_location`;
  const paintData = getPaintColorsV2(config.color, config.indexLimits, selectedItems);

  const layerOptions = {
    id: `layer_${id}`,
    paint: {
      'fill-color': paintData,
      'fill-opacity': config.opacity || 0.4,
      'fill-outline-color': paintData
    },
    type: 'fill'
  };

  const data: FeatureCollection = {
    features:
      layerMapData?.map((item: LayerMapData) => {
        return feature(item.polygon as Polygon, {
          elementId: item.elementId,
          name: item.name,
          source: getSourceId(item.elementId, poiFilters),
          weight: config.audience ? item.w : TOTAL_WEIGHT
        });
      }) || [],
    type: 'FeatureCollection'
  };

  return (
    <Source key={`${id}_location`} {...({ data, generateId: true, id: sourceId, type: 'geojson' } as SourceProps)}>
      <ReactLayer key={`layer_${id}`} {...(layerOptions as LayerProps)} />
    </Source>
  );
}
